import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc, increment, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  FREE_CREDITS, SCHOLAR_CREDITS,
  FREE_DIAGNOSTICS_MONTH, SCHOLAR_DIAGNOSTICS_MONTH,
  SCHOLAR_PLANS_MONTH,
} from '../constants';

const PRO_CODES     = ['AASHER2025', 'LEARNPRO', 'FRIENDSOFLEARNABLE', 'BETAUSER'];
const SCHOLAR_CODES = ['SCHOLAR2025', 'LEARNSCHOLAR'];

const startOfMonth = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1);

export const useCredits = () => {
  const { user } = useAuth();
  const [credits, setCredits]   = useState(null);
  const [plan, setPlan]         = useState('free');
  const [loading, setLoading]   = useState(true);
  const [monthlyDiagnosticCount, setMonthlyDiagnosticCount] = useState(0);
  const [monthlyPlanCount, setMonthlyPlanCount]             = useState(0);

  const fetchCredits = useCallback(async () => {
    if (!user) return;
    try {
      const ref  = doc(db, 'users', user.uid);
      const snap = await getDoc(ref);
      const monthStart = startOfMonth();

      if (snap.exists()) {
        const data = snap.data();
        const userPlan   = data.plan || 'free';
        const lastRefill = data.creditsLastRefill ? new Date(data.creditsLastRefill) : null;
        setPlan(userPlan);
        setMonthlyDiagnosticCount(data.monthlyDiagnosticCount ?? 0);
        setMonthlyPlanCount(data.monthlyPlanCount ?? 0);

        if (userPlan === 'pro') {
          setCredits(Infinity);
          setLoading(false);
          return;
        }

        const maxCredits = userPlan === 'scholar' ? SCHOLAR_CREDITS : FREE_CREDITS;

        // Monthly refill
        if (!lastRefill || lastRefill < monthStart) {
          await updateDoc(ref, {
            credits: maxCredits,
            creditsLastRefill: new Date().toISOString(),
            monthlyDiagnosticCount: 0,
            monthlyPlanCount: 0,
          });
          setCredits(maxCredits);
          setMonthlyDiagnosticCount(0);
          setMonthlyPlanCount(0);
          setLoading(false);
          return;
        }

        setCredits(data.credits ?? maxCredits);
      } else {
        await setDoc(ref, {
          credits: FREE_CREDITS,
          plan: 'free',
          creditsLastRefill: new Date().toISOString(),
          monthlyDiagnosticCount: 0,
          monthlyPlanCount: 0,
        }, { merge: true });
        setCredits(FREE_CREDITS);
        setPlan('free');
      }
    } catch (e) {  }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const useCredit = useCallback(async () => {
    if (!user || plan === 'pro') return true;
    if (credits !== null && credits <= 0) return false;
    try {
      await updateDoc(doc(db, 'users', user.uid), { credits: increment(-1) });
      setCredits(c => (c === null ? null : c - 1));
      return true;
    } catch (e) {  return false; }
  }, [user, credits, plan]);

  const incrementDiagnostic = useCallback(async () => {
    if (!user || plan === 'pro') return;
    await updateDoc(doc(db, 'users', user.uid), { monthlyDiagnosticCount: increment(1) })
      .catch(() => {});
    setMonthlyDiagnosticCount(c => c + 1);
  }, [user, plan]);

  const incrementPlanCount = useCallback(async () => {
    if (!user || plan === 'pro') return;
    await updateDoc(doc(db, 'users', user.uid), { monthlyPlanCount: increment(1) })
      .catch(() => {});
    setMonthlyPlanCount(c => c + 1);
  }, [user, plan]);

  const redeemCode = useCallback(async (code) => {
    if (!user) return { success: false, message: 'Not logged in' };
    const upper = code.toUpperCase().trim();

    let newPlan = null;
    if (PRO_CODES.includes(upper))     newPlan = 'pro';
    if (SCHOLAR_CODES.includes(upper)) newPlan = 'scholar';
    if (!newPlan) return { success: false, message: 'Invalid code. Please check and try again.' };

    try {
      const updates = {
        plan: newPlan,
        proActivatedAt: new Date().toISOString(),
        proCode: upper,
      };
      if (newPlan !== 'pro') updates.credits = SCHOLAR_CREDITS;
      await updateDoc(doc(db, 'users', user.uid), updates);
      setPlan(newPlan);
      setCredits(newPlan === 'pro' ? Infinity : SCHOLAR_CREDITS);
      return { success: true, message: `${newPlan === 'pro' ? 'Pro' : 'Scholar'} unlocked! Enjoy your upgrade.` };
    } catch (e) { return { success: false, message: 'Something went wrong.' }; }
  }, [user]);

  // Limit helpers — used by pages to gate features
  const canUseDiagnostic = plan === 'pro'
    ? true
    : plan === 'scholar'
      ? monthlyDiagnosticCount < SCHOLAR_DIAGNOSTICS_MONTH
      : monthlyDiagnosticCount < FREE_DIAGNOSTICS_MONTH;

  const canGeneratePlan = plan === 'pro'
    ? true
    : plan === 'scholar'
      ? monthlyPlanCount < SCHOLAR_PLANS_MONTH
      : false;

  return {
    credits,
    plan,
    loading,
    useCredit,
    redeemCode,
    fetchCredits,
    incrementDiagnostic,
    incrementPlanCount,
    monthlyDiagnosticCount,
    monthlyPlanCount,
    canUseDiagnostic,
    canGeneratePlan,
    isPro:     plan === 'pro',
    isScholar: plan === 'scholar',
  };
};
